import React, { useCallback, useEffect, useState } from "react";
import Chip from "@mui/material/Chip";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckCircle from "@mui/icons-material/CheckCircle";
import { NestiFyzImportGameButton } from "./NestifyzImportGameButton";
import { NestiFyzGameCreateFlow } from "./NestifyzGameCreateFlow";
import { GameImportPart2 } from "./GameImportPart2";

/**
 * @param {object} props
 * @param {import("./parseExport").NestiFyzGame} props.game
 * @param {import("@seabrookstudios/pitch2table-core").GameSummary|undefined} props.matchedGame
 * @param {boolean} [props.batchImportActive]
 * @param {() => void} [props.onBatchImportComplete]
 * @param {() => void} [props.onBatchImportError]
 */
export const NestiFyzGamesListItem = ({
  game,
  matchedGame,
  batchImportActive = false,
  onBatchImportComplete,
  onBatchImportError,
}) => {
  const [importedGameId, setImportedGameId] = useState(
    /** @type {import("@seabrookstudios/types").GameId|undefined} */ (undefined)
  );
  const [importRunKey, setImportRunKey] = useState(0);

  const startAssetImport = useCallback((gameId) => {
    setImportedGameId(gameId);
    setImportRunKey((key) => key + 1);
  }, []);

  useEffect(() => {
    if (!matchedGame?.id) {
      return;
    }

    setImportedGameId((current) => current ?? matchedGame.id);
  }, [matchedGame?.id]);

  const showBatchCreate = batchImportActive && !importedGameId;

  return (
    <>
      <ListItem
        disableGutters
        secondaryAction={
          matchedGame ? (
            <Chip icon={<CheckCircle />} label="IMPORTED" color="success" />
          ) : (
            <NestiFyzImportGameButton NestiFyzGame={game} onCreate={startAssetImport} />
          )
        }
      >
        <ListItemText
          primary={game.title || "Untitled NestiFyz game"}
          secondary={
            <Stack component="span" spacing={0.5}>
              {matchedGame && (
                <Typography component="span" variant="body2" color="text.secondary" display="block">
                  Linked to: {matchedGame.name}
                </Typography>
              )}
              {!game.id && (
                <Typography component="span" variant="body2" color="warning.main" display="block">
                  Missing NestiFyz ID — cannot import. Check import
                </Typography>
              )}
            </Stack>
          }
        />
      </ListItem>
      {showBatchCreate && (
        <NestiFyzGameCreateFlow
          game={game}
          onReady={startAssetImport}
          onError={() => {
            onBatchImportError?.();
          }}
        />
      )}
      {importedGameId && (
        <GameImportPart2
          key={importRunKey}
          gameId={importedGameId}
          nestifyzGame={game}
          onComplete={batchImportActive ? onBatchImportComplete : undefined}
        />
      )}
    </>
  );
};
